// @ts-check
// "Enviar teste" da campanha de e-mail (spec/email.md seção 10.7; spec-2.0.0 K42 e RISCO-06): sempre para quem pede e,
// se quiser, para até 4 colegas da equipe, nunca para um endereço digitado. Com um lead escolhido, só quem pede recebe
// os dados dele (lidos com o papel de quem pede); os colegas recebem os dados de exemplo. Até 20 testes por hora. O
// teste sai pela Amazon SES, como a campanha, e marca a revisão como testada.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { sendEmailCampaignTest } from '../../data/email.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { currentWorkspaceMemberState } from '../../data/metadata-store.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { Button } from '../../ui/button.js';
import { Checkbox } from '../../ui/feedback.js';
import { enqueueSnackbar, showErrorSnackbar } from '../../ui/snackbar.js';
import { EmailDialog } from '../email-design/email-dialog.js';
import { SampleCardPicker } from '../email-design/email-preview-frame.js';
import { MAXIMUM_TEST_COLLEAGUES, testErrorMessage } from '../email-design/template-test-dialog.js';
import { t } from '../../i18n/index.js';

/**
 * @param {{ campaignId: string, campaignName: string, onClose: () => void, onSent?: () => void, beforeSend?: () => Promise<unknown> }} props
 *   beforeSend: o assistente grava o que falta antes do teste, para o teste sair com o conteúdo da tela
 */
export function EmailCampaignTestDialog({ campaignId, campaignName, onClose, onSent, beforeSend }) {
  const currentMember = currentWorkspaceMemberState.value;
  const [colleagueIds, setColleagueIds] = useState(/** @type {string[]} */ ([]));
  const [card, setCard] = useState(/** @type {{ id: string, label: string } | null} */ (null));
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    loadWorkspaceMembers();
  }, []);
  const colleagues = (workspaceMembersState.value ?? []).filter((member) => member.id !== currentMember?.id);

  const send = async () => {
    setIsSending(true);
    setError('');
    try {
      if (beforeSend) await beforeSend();
      const result = await sendEmailCampaignTest(campaignId, { sampleCardId: card?.id ?? null, workspaceMemberIds: colleagueIds });
      enqueueSnackbar({ variant: 'success', message: t(result.simulated ? 'emailCampaigns.toasts.testSimulated' : 'emailCampaigns.toasts.testSent', { list: result.sentTo.join(', ') }) });
      onSent?.();
      onClose();
    } catch (sendError) {
      const message = testErrorMessage(sendError);
      if (message) setError(message);
      else showErrorSnackbar(sendError);
    } finally {
      setIsSending(false);
    }
  };

  return html`<${EmailDialog} title=${t('emailCampaigns.test.title')} subtitle=${campaignName} onClose=${onClose} className="email-test-dialog" testId="email-campaign-test-dialog"
    footer=${html`
      <${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('emailCampaigns.test.send')} icon="IconSend" accent="blue" size="small" isLoading=${isSending} onClick=${send} />
    `}>
    <p class="ec-muted">${t('emailCampaigns.test.description')}</p>
    <div class="email-test-recipients">
      <div class="email-check-row" data-disabled="true">
        <${Checkbox} checked=${true} disabled=${true} ariaLabel=${t('emailCampaigns.test.toMe', { email: currentMember?.userEmail ?? '' })} />
        <span>${t('emailCampaigns.test.toMe', { email: currentMember?.userEmail ?? '' })}</span>
      </div>
      <p class="email-section-label">${t('emailCampaigns.test.colleagues')}</p>
      <ul class="email-member-list">
        ${colleagues.map((member) => {
          const checked = colleagueIds.includes(member.id);
          const disabled = !checked && colleagueIds.length >= MAXIMUM_TEST_COLLEAGUES;
          return html`<li key=${member.id}>
            <div class="email-check-row" data-disabled=${disabled ? 'true' : undefined}>
              <${Checkbox} checked=${checked} disabled=${disabled} ariaLabel=${formatFullName(member.name) || member.userEmail || ''}
                onChange=${(/** @type {boolean} */ next) => setColleagueIds((current) => (next ? [...current, member.id] : current.filter((id) => id !== member.id)))} />
              <span>${formatFullName(member.name) || member.userEmail}</span>
              ${member.userEmail ? html`<span class="email-muted">${member.userEmail}</span>` : null}
            </div>
          </li>`;
        })}
      </ul>
    </div>
    <${SampleCardPicker} value=${card} onChange=${setCard} />
    <p class="input-hint">${t('emailCampaigns.test.sampleCardHint')}</p>
    ${error ? html`<p class="input-hint" data-danger="true" role="alert">${error}</p>` : null}
  <//>`;
}
